import { parseLocalDateInput } from '../../../utils/dateRangeUtils'
import ChartPanel from '../../charts/ChartPanel'
import './GoalPerformanceTable.css'

function displayStatus(status) {
  if (status === 'exceeded') return { key: 'on-track', label: 'Achieved' }
  if (status === 'on-track') return { key: 'on-track', label: 'On track' }
  if (status === 'needs-attention' || status === 'behind') {
    return { key: 'needs-attention', label: 'Needs Attention' }
  }
  return { key: 'at-risk', label: 'At Risk' }
}

function formatDurationLabel(goal) {
  if (goal?.durationLabel) return goal.durationLabel
  const period = String(goal?.period ?? 'monthly').trim().toLowerCase()
  const start =
    parseLocalDateInput(goal?.periodStart) ||
    parseLocalDateInput(new Date())
  if (!start) return '—'
  if (period === 'quarterly' || period === 'quarter') {
    const quarter = Math.floor(start.getMonth() / 3) + 1
    return `Q${quarter} ${start.getFullYear()}`
  }
  if (period === 'annual' || period === 'annually' || period === 'yearly' || period === 'year') {
    return String(start.getFullYear())
  }
  return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

function progressPercent(goal) {
  if (goal?.progressRatio == null || Number.isNaN(Number(goal.progressRatio))) return 0
  return Math.round(Number(goal.progressRatio) * 100)
}

function GoalPerformanceTable({
  goals = [],
  formatGoalValue,
  loading = false,
  onManageGoals,
}) {
  const rows = Array.isArray(goals) ? goals : []
  const onTrackCount = rows.filter((goal) => {
    const key = displayStatus(goal.status).key
    return key === 'on-track'
  }).length
  const needsAttentionCount = rows.filter(
    (goal) => displayStatus(goal.status).key === 'needs-attention',
  ).length
  const atRiskCount = Math.max(0, rows.length - onTrackCount - needsAttentionCount)

  return (
    <ChartPanel
      dataChartId="goalPerformance"
      className="goalPerformancePanel"
      size="sm"
      loading={loading}
      loadingMessage="Loading goal progress…"
      title="Goal Performance"
      subtitle="Track progress against your active goals."
      actions={
        typeof onManageGoals === 'function' ? (
          <button type="button" className="goalPerformanceManageLink" onClick={onManageGoals}>
            Manage goals
          </button>
        ) : null
      }
    >
      <div className="goalPerformanceSummary">
        <span className="goalPerformanceSummaryCount">
          {rows.length} metric{rows.length === 1 ? '' : 's'}
        </span>
        {rows.length > 0 && (
          <>
            <span className="goalPerformancePill goalPerformancePill--onTrack">
              {onTrackCount} on track
            </span>
            {needsAttentionCount > 0 && (
              <span className="goalPerformancePill goalPerformancePill--behind">
                {needsAttentionCount} needs attention
              </span>
            )}
            {atRiskCount > 0 && (
              <span className="goalPerformancePill goalPerformancePill--atRisk">
                {atRiskCount} at risk
              </span>
            )}
          </>
        )}
      </div>

      <div
        className={`goalPerformanceTable${rows.length === 0 ? ' goalPerformanceTable--empty' : ''}`}
        role="table"
        aria-label="Goal performance"
      >
        <div className="goalPerformanceRow goalPerformanceRow--header" role="row">
          <div role="columnheader">Metric</div>
          <div role="columnheader">Duration</div>
          <div role="columnheader">Progress</div>
          <div role="columnheader">Actual / Target</div>
          <div role="columnheader">Status</div>
        </div>
        {rows.length === 0 ? (
          <div className="goalPerformanceEmpty" role="status">
            No active goals
          </div>
        ) : (
          rows.map((goal) => {
            const pct = progressPercent(goal)
            const barWidth = Math.min(100, Math.max(0, pct))
            const status = displayStatus(goal.status)
            const actual = goal.current
            const actualLabel = formatGoalValue ? formatGoalValue(actual, goal) : String(actual ?? '—')
            const targetLabel = formatGoalValue ? formatGoalValue(goal.target, goal) : String(goal.target ?? '—')
            return (
              <div key={goal.id} className="goalPerformanceRow" role="row">
                <div className="goalPerformanceMetric" role="cell">
                  <span className="goalPerformanceMetricName">{goal.name}</span>
                  {goal.platform ? (
                    <span className="goalPerformanceMetricPlatform">{goal.platform}</span>
                  ) : null}
                </div>
                <div className="goalPerformanceDuration" role="cell">
                  {formatDurationLabel(goal)}
                </div>
                <div className="goalPerformanceProgress" role="cell">
                  <div className="goalPerformanceBarTrack" aria-hidden="true">
                    <div
                      className={`goalPerformanceBarFill goalPerformanceBarFill--${status.key}`}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                  <span className="goalPerformanceProgressValue dashboard-tabular-nums">{pct}%</span>
                </div>
                <div className="goalPerformanceActual dashboard-tabular-nums" role="cell">
                  {actualLabel} / {targetLabel}
                </div>
                <div role="cell">
                  <span className={`goalPerformanceStatus goalPerformanceStatus--${status.key}`}>
                    {status.label}
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>
    </ChartPanel>
  )
}

export default GoalPerformanceTable
